import {Formik} from 'formik';
import {Button, Group} from '@mantine/core';
import {MdCancel, MdCheck, MdClear} from 'react-icons/md';
import {FilterFormComponent} from './FilterFormComponent';
import {FilterFormSchema} from './FilterFormSchema';
import {toFormikValidationSchema} from 'zod-formik-adapter';
import {useMeteringRecordsFilter} from '../../hooks/useMeteringRecords';

interface FilterFormProps {
    onApply: () => void;
    onCancel: () => void;
}

// Every field the drawer renders (FilterFormComponent) must be read back here, or an
// applied filter shows blank on reopen and Apply silently drops it (FNP-1890).
interface FilterFormValues {
    state?: string;
    customerId?: string;
    meterId?: string;
    meterCode?: string;
    periodStartFrom?: string;
    periodStartTo?: string;
}

export const FilterForm = ({onApply, onCancel}: FilterFormProps) => {
    const {filters, handleSetFilters, handleClearFilters} = useMeteringRecordsFilter();

    const initialValues: FilterFormValues = {
        state: filters.state || '',
        customerId: filters.customerId || '',
        meterId: filters.meterId || '',
        meterCode: filters.meterCode || '',
        periodStartFrom: filters.periodStartFrom || '',
        periodStartTo: filters.periodStartTo || '',
    };

    const onSubmit = (values: FilterFormValues) => {
        const clean = Object.entries(values).reduce((acc, [key, value]) => {
            if (value && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string>);

        handleSetFilters(clean);
        onApply();
    };

    return (
        <Formik<FilterFormValues>
            initialValues={initialValues}
            validationSchema={toFormikValidationSchema(FilterFormSchema)}
            onSubmit={onSubmit}
        >
            {({handleSubmit, resetForm}) => (
                <>
                    <FilterFormComponent/>

                    <Group justify="space-between" mt="xl">
                        <Button
                            variant="outline"
                            leftSection={<MdCancel/>}
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Group gap="xs">
                            <Button
                                variant="outline"
                                leftSection={<MdClear/>}
                                onClick={() => {
                                    resetForm();
                                    handleClearFilters();
                                }}
                            >
                                Clear
                            </Button>
                            <Button
                                leftSection={<MdCheck/>}
                                onClick={() => handleSubmit()}
                            >
                                Apply
                            </Button>
                        </Group>
                    </Group>
                </>
            )}
        </Formik>
    );
};
